import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Video, Play, Volume2, VolumeX } from 'lucide-react';
import { useFullscreen } from '../contexts/FullscreenContext';

const TIMER_VIDEO_CONTAINER_ID = 'timer-video-player';

export default function TimerVideoModal({
  isOpen,
  onClose,
  onStop,
  videoId,
  videoTitle,
  youtubeUrl,
  volume = 70,
  onVolumeChange,
  // YouTube播放器相关props
  ytApiReady,
  showPlayPrompt,
  createYouTubePlayer,
  manualStartPlay,
  stopVideo
}) {
  const { isFullscreen, enterFullscreen, exitFullscreen } = useFullscreen();
  const modalRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isModalFullscreen, setIsModalFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration] = useState(0);
  const playerRef = useRef(null);

  // 创建YouTube播放器
  useEffect(() => {
    if (
      isOpen &&
      videoId &&
      ytApiReady &&
      typeof createYouTubePlayer === 'function'
    ) {
      console.log('Creating YouTube player for timer video modal');
      
      // 使用固定的容器ID，与Video Alarms保持一致
      setTimeout(() => {
        createYouTubePlayer(TIMER_VIDEO_CONTAINER_ID, {
          allowFullscreen: true,
          autoplay: 1
        });
      }, 100);

      return () => {
        if (stopVideo) {
          stopVideo();
        }
        playerRef.current = null;
      };
    }
  }, [isOpen, videoId, ytApiReady, createYouTubePlayer, stopVideo]);

  // 定义处理函数
  const handlePlayPause = useCallback(() => {
    if (playerRef.current) {
      // 简化播放控制，直接调用YouTube API
      if (playerRef.current.getPlayerState && playerRef.current.getPlayerState() === 1) {
        playerRef.current.pauseVideo();
      } else {
        playerRef.current.playVideo();
      }
    }
  }, []);

  const handleMute = useCallback(() => {
    if (playerRef.current) {
      if (isMuted) {
        playerRef.current.unMute();
        playerRef.current.setVolume(volume);
      } else {
        playerRef.current.mute();
      }
      setIsMuted(!isMuted);
    }
  }, [isMuted, volume]);

  const handleSeek = useCallback((time) => {
    if (playerRef.current && playerRef.current.seekTo) {
      playerRef.current.seekTo(time);
      setCurrentTime(time);
    }
  }, []);

  const handleModalFullscreen = useCallback(() => {
    if (isModalFullscreen) {
      setIsModalFullscreen(false);
      if (isFullscreen) {
        exitFullscreen();
      }
    } else {
      setIsModalFullscreen(true);
      if (modalRef.current) {
        enterFullscreen(modalRef.current);
      }
    }
  }, [isModalFullscreen, isFullscreen, exitFullscreen, enterFullscreen]);

  // 键盘快捷键
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'Escape':
          if (isModalFullscreen) {
            handleModalFullscreen();
          } else {
            onClose();
          }
          break;
        case 'f':
        case 'F':
          handleModalFullscreen();
          break;
        case 'm':
        case 'M':
          handleMute();
          break;
        default:
          // 其他按键不处理
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, isModalFullscreen, handlePlayPause, handleModalFullscreen, handleMute, onClose]);

  const handleVolumeChange = (newVolume) => {
    if (playerRef.current) {
      playerRef.current.setVolume(newVolume);
      if (newVolume === 0) {
        setIsMuted(true);
      } else if (isMuted) {
        setIsMuted(false);
      }
    }
    if (onVolumeChange) {
      onVolumeChange(newVolume);
    }
  };

  const handleManualVideoPlay = () => {
    if (manualStartPlay) {
      manualStartPlay(TIMER_VIDEO_CONTAINER_ID);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };


  const handleStop = () => {
    if (stopVideo) {
      stopVideo();
    }
    onStop();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div 
        ref={modalRef}
        className={`bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden transition-all duration-300 ${
          isModalFullscreen 
            ? 'w-full h-full rounded-none' 
            : 'w-full max-w-4xl mx-4 max-h-[90vh]'
        }`}
      >
        {/* 标题栏 */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 flex items-center">
          <div className="flex items-center gap-3">
            <Video className="w-6 h-6 text-white" />
            <div>
              <h3 className="text-xl font-bold text-white">Timer Video Alarm</h3>
              {videoTitle && (
                <p className="text-red-100 text-sm">{videoTitle}</p>
              )}
            </div>
          </div>
        </div>

        {/* 视频播放区域 */}
        <div className="relative bg-black">
          {videoId ? (
            <div className="relative">
              {/* YouTube播放器容器 - 使用固定ID */}
              <div 
                id={TIMER_VIDEO_CONTAINER_ID}
                className={`w-full bg-black ${
                  isModalFullscreen ? 'h-[calc(100vh-200px)]' : 'aspect-video'
                }`}
                style={{ minHeight: isModalFullscreen ? 'calc(100vh - 200px)' : '450px' }}
              />

              {/* 手动播放提示 */}
              {showPlayPrompt && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <div className="text-center text-white space-y-4">
                    <div className="w-16 h-16 mx-auto bg-white/20 rounded-full flex items-center justify-center">
                      <Play className="w-8 h-8" />
                    </div>
                    <p className="text-lg font-semibold">⚠️ Video cannot autoplay</p>
                    <p className="text-sm text-white/80 max-w-md">
                      Due to browser autoplay policy, please click the button below to manually start playing the video
                    </p>
                    <button
                      onClick={handleManualVideoPlay}
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                    >
                      🎵 Play Video Manually
                    </button>
                  </div>
                </div>
              )}

              {/* 加载状态 */}
              {!ytApiReady && (
                <div className="absolute inset-0 bg-black flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p>Loading YouTube player...</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
                          <div className="aspect-video bg-gray-800 flex items-center justify-center">
              <div className="text-center text-gray-400">
                <Video className="w-16 h-16 mx-auto mb-4" />
                <p className="text-lg">No video set</p>
                <p className="text-sm">Please enter a YouTube video link in timer settings</p>
              </div>
            </div>
          )}
        </div>

        {/* 控制栏 */}
        <div className="bg-gray-800 px-6 py-4 space-y-4">
          {/* 进度条 */}
          {duration > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <span>{formatTime(currentTime)}</span>
                <div className="flex-1 relative">
                  <input
                    type="range"
                    min="0"
                    max={duration}
                    value={currentTime}
                    onChange={(e) => handleSeek(Number(e.target.value))}
                    className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${(currentTime / duration) * 100}%, #4b5563 ${(currentTime / duration) * 100}%, #4b5563 100%)`
                    }}
                  />
                </div>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          )}

          {/* 播放控制 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* 音量控制 */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleMute}
                  className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors"
                  title="Mute/Unmute (M)"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={isMuted ? 0 : volume}
                  onChange={(e) => handleVolumeChange(Number(e.target.value))}
                  className="w-24 h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-white text-sm w-8">{isMuted ? 0 : volume}</span>
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleStop}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
              >
                STOP
              </button>
            </div>
          </div>

          {/* 视频信息 */}
          {youtubeUrl && (
            <div className="pt-2 border-t border-gray-700">
              <p className="text-gray-400 text-sm">
                <span className="font-medium">Video Link:</span> {youtubeUrl}
              </p>
            </div>
          )}


        </div>
      </div>
    </div>
  );
}